import { cva, type VariantProps } from 'class-variance-authority';
import { type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

/*
 * Status kit — hairline outline badges + status dots.
 *
 * PHO-14461 Part C: Phoebe purple owns the primary solid callout, the
 * purple status dot, the active-tab underline, and focus-ring
 * highlights. Badges never render purple text; the `accent`/`purple`
 * tone was removed from the badge API and lives on AdminStatusDot only.
 *
 * The `solid` variant is opt-in and restricted to two callout tones:
 *   - `ink-primary` (default): Phoebe purple fill on white — primary
 *     chip. Kept under the `ink-primary` key for API stability with
 *     existing consumers; the visual is now purple.
 *   - `danger`: danger fill on white — severe callouts only.
 * All other tones would clash with the editorial paper aesthetic.
 *
 * Pages must not hand-pick green/red utilities; go through this
 * component so the palette stays disciplined.
 */

const BADGE_BASE = cn(
    'inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap',
    'rounded-admin-sm h-5 px-2 text-admin-xs font-medium uppercase',
    'tracking-[0.06em] [&_svg]:size-3 [&_svg]:shrink-0',
);

export type AdminBadgeTone =
    | 'neutral'
    | 'ok'
    | 'warn'
    | 'danger'
    | 'info'
    | 'outline';

export type AdminBadgeSolidTone = 'ink-primary' | 'danger';

const OUTLINE_TONE_CLASSES: Record<AdminBadgeTone, string> = {
    neutral: 'border-admin-border text-admin-neutral-text',
    ok: 'border-admin-border text-admin-ok-text',
    warn: 'border-admin-border text-admin-warn-text',
    danger: 'border-admin-border text-admin-danger-text',
    info: 'border-admin-border text-admin-info-text',
    outline: 'border-admin-border text-admin-ink-secondary',
};

const SOLID_TONE_CLASSES: Record<AdminBadgeSolidTone, string> = {
    'ink-primary': 'border-transparent bg-admin-purple text-white',
    danger: 'border-transparent bg-admin-danger-text text-white',
};

const adminBadgeVariants = cva(BADGE_BASE, {
    variants: {
        variant: {
            outline: 'border bg-transparent',
            solid: '',
        },
    },
    defaultVariants: {
        variant: 'outline',
    },
});

type AdminBadgeVariantProps = VariantProps<typeof adminBadgeVariants>;

type OutlineBadgeProps = React.ComponentProps<'span'> &
    AdminBadgeVariantProps & {
        readonly variant?: 'outline';
        readonly tone?: AdminBadgeTone;
    };

type SolidBadgeProps = React.ComponentProps<'span'> &
    AdminBadgeVariantProps & {
        readonly variant: 'solid';
        readonly tone?: AdminBadgeSolidTone;
    };

export function AdminBadge(props: OutlineBadgeProps | SolidBadgeProps) {
    const { className, variant, tone, ...rest } = props;
    const resolvedVariant = variant ?? 'outline';
    if (resolvedVariant === 'solid') {
        // Solid callouts default to the ink-primary treatment. The
        // `tone` union is narrower here so downstream callers cannot
        // request a warn/info/outline solid — those would clash with
        // the ink-on-paper aesthetic.
        const solidTone: AdminBadgeSolidTone =
            (tone as AdminBadgeSolidTone | undefined) ?? 'ink-primary';
        return (
            <span
                data-slot="admin-badge"
                data-variant="solid"
                data-tone={solidTone}
                className={cn(
                    adminBadgeVariants({ variant: 'solid' }),
                    SOLID_TONE_CLASSES[solidTone],
                    className,
                )}
                {...rest}
            />
        );
    }
    const outlineTone: AdminBadgeTone =
        (tone as AdminBadgeTone | undefined) ?? 'neutral';
    return (
        <span
            data-slot="admin-badge"
            data-variant="outline"
            data-tone={outlineTone}
            className={cn(
                adminBadgeVariants({ variant: 'outline' }),
                OUTLINE_TONE_CLASSES[outlineTone],
                className,
            )}
            {...rest}
        />
    );
}

export type AdminStatusDotTone = AdminBadgeTone | 'purple';

const STATUS_DOT_TONE_CLASSES: Record<AdminStatusDotTone, string> = {
    neutral: 'bg-admin-ink-faint',
    ok: 'bg-admin-ok-text',
    warn: 'bg-admin-warn-text',
    danger: 'bg-admin-danger-text',
    info: 'bg-admin-info-text',
    outline: 'bg-admin-ink-faint',
    purple: 'bg-admin-purple',
};

export function AdminStatusDot({
    tone = 'neutral',
    label,
    className,
    ...props
}: React.ComponentProps<'span'> & {
    readonly tone?: AdminStatusDotTone;
    readonly label: ReactNode;
}) {
    return (
        <span
            data-slot="admin-status-dot"
            className={cn(
                'text-admin-sm text-admin-ink-secondary inline-flex items-center gap-1.5',
                className,
            )}
            {...props}
        >
            <span
                aria-hidden="true"
                data-testid="admin-status-dot-indicator"
                className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    STATUS_DOT_TONE_CLASSES[tone],
                )}
            />
            {label}
        </span>
    );
}
